import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  IconButton,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  CircularProgress,
  Grid2
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/authService';
import InputField from '../components/common/FormFields/InputField';
import SelectField from '../components/common/FormFields/SelectField';

// Define interfaces for the API data
interface Category {
  _id: string;
  name: string;
  description: string;
}

interface Knowledge {
  _id: string;
  title: string;
  description: string;
  duration: number;
  category: {
    _id: string;
    name: string;
  };
  videoUrl1: string;
  videoUrl2?: string;
  status: 'DRAFT' | 'PUBLISHED';
  isActive: boolean;
  createdBy: string;
  objectId: string;
  createdAt: string;
  updatedAt: string;
}

interface KnowledgeUpdateInput {
  title: string;
  description: string;
  duration: number;
  category: string;
  videoUrl1: string;
  videoUrl2?: string;
  status: 'DRAFT' | 'PUBLISHED';
  isActive: boolean;
}

const LearningEdit: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [duration, setDuration] = useState<number>(30);
  const [categoryId, setCategoryId] = useState<string>('');
  const [videoUrl1, setVideoUrl1] = useState<string>('');
  const [videoUrl2, setVideoUrl2] = useState<string>('');
  const [status, setStatus] = useState<'DRAFT' | 'PUBLISHED'>('DRAFT');
  const [isActive, setIsActive] = useState<boolean>(true);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/knowledge-categories');
        setCategories(response.data.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setSnackbar({
          open: true,
          message: 'ไม่สามารถดึงข้อมูลหมวดหมู่ได้',
          severity: 'error',
        });
      }
    };
    
    fetchCategories();
  }, []);
  
  // Fetch knowledge data
  useEffect(() => {
    const fetchKnowledge = async () => {
      if (!id) return;
      
      setInitialLoading(true);
      try {
        const response = await api.get(`/knowledge/${id}`);
        const knowledge: Knowledge = response.data;
        
        // Set form values
        setTitle(knowledge.title);
        setDescription(knowledge.description || '');
        setDuration(knowledge.duration);
        setCategoryId(knowledge.category._id);
        setVideoUrl1(knowledge.videoUrl1);
        setVideoUrl2(knowledge.videoUrl2 || '');
        setStatus(knowledge.status);
        setIsActive(knowledge.isActive);
      } catch (error) {
        console.error('Error fetching knowledge:', error);
        setSnackbar({
          open: true,
          message: 'ไม่สามารถดึงข้อมูลบทเรียนได้',
          severity: 'error',
        });
        // Navigate back to list if knowledge not found
        setTimeout(() => {
          navigate('/learning');
        }, 2000);
      } finally {
        setInitialLoading(false);
      }
    };
    
    fetchKnowledge();
  }, [id, navigate]);
  
  const handleBack = () => {
    navigate('/learning');
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!title) {
      setSnackbar({
        open: true,
        message: 'กรุณาระบุชื่อบทเรียน',
        severity: 'error',
      });
      return;
    }
    
    if (!videoUrl1) {
      setSnackbar({
        open: true,
        message: 'กรุณาระบุลิงก์วิดีโอหลัก',
        severity: 'error',
      });
      return;
    }
    
    if (!categoryId) {
      setSnackbar({
        open: true,
        message: 'กรุณาเลือกหมวดหมู่',
        severity: 'error',
      });
      return;
    }
    
    setLoading(true);
    
    // Prepare data for API
    const knowledgeData: KnowledgeUpdateInput = {
      title,
      description,
      duration,
      category: categoryId,
      videoUrl1,
      videoUrl2: videoUrl2 || undefined,
      status,
      isActive
    };
    
    try {
      // Call the API endpoint to update knowledge
      await api.patch(`/knowledge/${id}`, knowledgeData);
      
      setSnackbar({
        open: true,
        message: 'แก้ไขบทเรียนสำเร็จ',
        severity: 'success',
      });
      
      // Wait for snackbar to show before navigating
      setTimeout(() => {
        navigate('/learning');
      }, 1500);
    } catch (error) {
      console.error('Error updating knowledge:', error);
      setSnackbar({
        open: true,
        message: 'เกิดข้อผิดพลาดในการแก้ไขบทเรียน',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async () => {
    if (!id) return;
    
    if (!window.confirm('คุณต้องการลบบทเรียนนี้ใช่หรือไม่?')) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Call the API endpoint to delete knowledge
      await api.delete(`/knowledge/${id}`);
      
      setSnackbar({
        open: true,
        message: 'ลบบทเรียนสำเร็จ',
        severity: 'success',
      });
      
      // Wait for snackbar to show before navigating
      setTimeout(() => {
        navigate('/learning');
      }, 1500);
    } catch (error) {
      console.error('Error deleting knowledge:', error);
      setSnackbar({
        open: true,
        message: 'เกิดข้อผิดพลาดในการลบบทเรียน',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancel = () => {
    navigate('/learning');
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false,
    }));
  };
  
  if (initialLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1">
          แก้ไขบทเรียนออนไลน์
        </Typography>
      </Box>
      
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 800, mx: 'auto' }}>
          <Grid2 container spacing={4}>
            <Grid2 size={12}>
              <Typography variant="h6" gutterBottom>
                ข้อมูลบทเรียน
              </Typography>
            </Grid2>
            <Grid2 size={12}>
              <InputField
                label="ชื่อบทเรียน"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                name="title"
              />
            </Grid2>
            <Grid2 size={12}>
              <InputField
                label="คำอธิบายบทเรียน"
                multiline
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                name="description"
              />
            </Grid2>
            <Grid2 size={12}>
              <SelectField
                label="หมวดหมู่"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                required
                name="category"
                options={categories.map((category) => ({
                  value: category._id,
                  label: category.name
                }))}
              />
            </Grid2>
            <Grid2 size={12}>
              <InputField
                label="ระยะเวลา (นาที)"
                type="number"
                value={duration.toString()}
                onChange={(e) => setDuration(parseInt(e.target.value, 10))}
                required
                name="duration"
              />
            </Grid2>
            <Grid2 size={12}>
              <InputField
                label="ลิงก์วิดีโอหลัก"
                value={videoUrl1}
                onChange={(e) => setVideoUrl1(e.target.value)}
                required
                name="videoUrl1"
              />
            </Grid2>
            <Grid2 size={12}>
              <InputField
                label="ลิงก์วิดีโอเสริม (ถ้ามี)"
                value={videoUrl2}
                onChange={(e) => setVideoUrl2(e.target.value)}
                name="videoUrl2"
              />
            </Grid2>
            <Grid2 size={12}>
              <SelectField
                label="สถานะ"
                value={status}
                onChange={(e) => setStatus(e.target.value as 'DRAFT' | 'PUBLISHED')}
                required
                name="status"
                options={[
                  { value: 'DRAFT', label: 'แบบร่าง' },
                  { value: 'PUBLISHED', label: 'เผยแพร่' }
                ]}
              />
            </Grid2>
            <Grid2 size={12}>
              <SelectField
                label="การใช้งาน"
                value={isActive ? "true" : "false"}
                onChange={(e) => setIsActive(e.target.value === "true")}
                required
                name="isActive"
                options={[
                  { value: 'true', label: 'เปิดการใช้งาน' },
                  { value: 'false', label: 'ปิดการใช้งาน' }
                ]}
              />
            </Grid2>
          </Grid2>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              variant="outlined"
              color="error"
              onClick={handleDelete}
              disabled={loading}
            >
              ลบบทเรียน
            </Button>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={loading}
              >
                ยกเลิก
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading}
              >
                {loading ? 'กำลังบันทึก...' : 'บันทึก'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LearningEdit; 